import type {FetchBaseQueryError} from '@reduxjs/toolkit/query';

import {isNil, isNotNil} from 'ramda';

import {invalidateOnSuccess} from '@dms/api/base';

import {
  type ApplyMidYearPriceAdjustmentApiArg,
  type ArchiveColourApiArg,
  type ArchivePackageApiArg,
  type ArchiveTrimApiArg,
  type ArchiveUpholsteryApiArg,
  type ArchiveVariantApiArg,
  type CatalogueImageOwner,
  type CreatePackageApiArg,
  type CreateTrimApiArg,
  type CreateUpholsteryApiArg,
  type CreateVariantApiArg,
  type ExportConfigurationOfferSheetApiArg,
  type FactoryReferenceInUseResponseBody,
  type UploadCatalogueImageApiArg,
  type UploadCatalogueImageApiResponse,
  newVehicleGeneratedApi,
  type PatchVariantMatrixCellApiArg,
  type PutColourTrimsApiArg,
  type PutVariantMatrixApiArg,
  type UpdatePackageApiArg,
  type UpdateTrimApiArg,
  type UpdateVariantApiArg,
  type VehicleOrderDetailResponseBody,
} from './codegen/__generated__/api.generated';

const FACTORY_REFERENCE_IN_USE = 'DMS-NV-VO-0015';
const FACTORY_REFERENCE_LOCKED = 'DMS-NV-VO-0016';

export const isNewVehicleConflict = (
  error: unknown
): error is FetchBaseQueryError & {status: 409} =>
  typeof error === 'object' && error !== null && 'status' in error && error.status === 409;

const hasErrorCode = (data: object, code: string) =>
  'errors' in data &&
  Array.isArray(data.errors) &&
  data.errors.some(
    (item: unknown) =>
      typeof item === 'object' && item !== null && 'code' in item && item.code === code
  );

/**
 * The 409 of a placement whose factory reference another active order of the make holds. Its body
 * names the owner order and its vehicle; every other 409 of the same endpoints is a generic
 * conflict.
 */
export const isFactoryReferenceInUse = (
  error: unknown
): error is FetchBaseQueryError & {status: 409; data: FactoryReferenceInUseResponseBody} => {
  if (!isNewVehicleConflict(error) || typeof error.data !== 'object' || error.data === null) {
    return false;
  }
  const {data} = error;
  return (
    'vehicleOrderId' in data &&
    typeof data.vehicleOrderId === 'string' &&
    'vehicleId' in data &&
    typeof data.vehicleId === 'string' &&
    hasErrorCode(data, FACTORY_REFERENCE_IN_USE)
  );
};

/** The 409 of a factory reference change on an order that was already handed over. */
export const isFactoryReferenceLocked = (
  error: unknown
): error is FetchBaseQueryError & {status: 409} =>
  isNewVehicleConflict(error) &&
  typeof error.data === 'object' &&
  error.data !== null &&
  hasErrorCode(error.data, FACTORY_REFERENCE_LOCKED);

export const isDefiniteNewVehicleRejection = (error: unknown) => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return false;
  }
  const {status} = error;
  return typeof status === 'number' && status >= 400 && status < 500 && status !== 408;
};

type CatalogueResource =
  | 'trim'
  | 'engine'
  | 'variant'
  | 'colour'
  | 'upholstery'
  | 'feature'
  | 'package'
  | 'assignment'
  | 'rule'
  | 'restriction'
  | 'matrix'
  | 'bundle'
  | 'evaluate'
  | 'modelYear'
  | 'readiness';

const CONTENT_DEPENDENTS: CatalogueResource[] = ['bundle', 'evaluate', 'readiness'];

const resourceTag = (id: CatalogueResource) => ({type: 'NewVehicleCatalogueResource' as const, id});

const lockedChildWrite = (...resources: CatalogueResource[]) => ({
  invalidatesTags: [...resources, ...CONTENT_DEPENDENTS].map(resourceTag),
});

/** What any write to a trim invalidates, besides the lists it changes. */
const TRIM_WRITE_TAGS = lockedChildWrite('trim').invalidatesTags;

/** One trim's assignment list, so a trim's PUT refetches only that trim's list. */
const trimAssignmentTag = (trimId: string) => ({
  type: 'NewVehicleCatalogueResource' as const,
  id: `assignment:${trimId}`,
});

const catalogueWrite = (...resources: CatalogueResource[]) =>
  lockedChildWrite(...resources, 'modelYear');

/**
 * A create or archive of a priced item, or a status or enable switch, that carried a new pricelist version
 * also rewrote the model year (label and version) and added a History entry (D88), so a successful one
 * invalidates both broad tags, as an applied price adjustment does.
 */
const pricedItemWrite = <Arg>(
  pricelistVersionOf: (arg: Arg) => unknown,
  ...resources: CatalogueResource[]
) => ({
  invalidatesTags: (_result: unknown, error: unknown, arg: Arg) => [
    ...[...resources, ...CONTENT_DEPENDENTS].map(resourceTag),
    ...(isNil(error) && isNotNil(pricelistVersionOf(arg))
      ? (['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'] as const)
      : []),
  ],
});

const catalogueImageTags = invalidateOnSuccess((_result, {owner}: {owner: CatalogueImageOwner}) =>
  owner === 'model'
    ? ['NewVehicleCatalogue' as const, 'NewVehicleCatalogueAuthoring' as const]
    : [resourceTag(owner), resourceTag('bundle')]
);

export const newVehicleApi = newVehicleGeneratedApi.enhanceEndpoints({
  addTagTypes: [
    'NewVehicleConfiguration',
    'NewVehicleCatalogue',
    'NewVehicleOrder',
    'NewVehicleCatalogueAuthoring',
    'NewVehicleCatalogueResource',
    'NewVehicleModelYearReference',
  ],
  endpoints: {
    getCatalogueTree: {
      providesTags: ['NewVehicleCatalogue', resourceTag('modelYear'), resourceTag('bundle')],
    },
    getConfiguratorBundle: {
      providesTags: ['NewVehicleCatalogue', resourceTag('modelYear'), resourceTag('bundle')],
    },
    getConfigurationDetail: {providesTags: ['NewVehicleConfiguration']},
    getConfigurationCustomerView: {providesTags: ['NewVehicleConfiguration']},
    listInterestConfigurations: {providesTags: ['NewVehicleConfiguration']},
    listBusinessCaseConfigurations: {providesTags: ['NewVehicleConfiguration']},
    createConfiguration: {
      invalidatesTags: ['NewVehicleConfiguration', 'NewVehicleModelYearReference'],
    },
    updateConfiguration: {
      invalidatesTags: ['NewVehicleConfiguration', 'NewVehicleModelYearReference'],
    },
    deleteConfiguration: {
      invalidatesTags: ['NewVehicleConfiguration', 'NewVehicleModelYearReference'],
    },
    copyConfiguration: {
      invalidatesTags: ['NewVehicleConfiguration', 'NewVehicleModelYearReference'],
    },
    repriceConfiguration: {
      invalidatesTags: ['NewVehicleConfiguration'],
    },
    orderConfiguration: {
      invalidatesTags: ['NewVehicleConfiguration', 'NewVehicleOrder'],
    },
    registerExternalOrder: {
      invalidatesTags: ['NewVehicleConfiguration', 'NewVehicleOrder'],
    },
    getVehicleOrder: {providesTags: ['NewVehicleOrder']},
    getVehicleOrderFactoryRecord: {providesTags: ['NewVehicleOrder']},
    updateOrderStatus: {
      invalidatesTags: ['NewVehicleOrder', 'NewVehicleConfiguration'],
    },
    cancelOrder: {
      invalidatesTags: (result) => [
        'NewVehicleOrder',
        'NewVehicleConfiguration',
        ...(result
          ? [{type: 'Vehicle' as const, id: result.vehicleId}, 'SaleVehicle' as const]
          : []),
      ],
    },
    recordOrderArrival: {
      invalidatesTags: (result) => [
        'NewVehicleOrder',
        'NewVehicleConfiguration',
        ...(result
          ? [{type: 'Vehicle' as const, id: result.vehicleId}, 'SaleVehicle' as const]
          : []),
      ],
    },
    handoverOrder: {
      invalidatesTags: (result) => [
        'NewVehicleOrder',
        'NewVehicleConfiguration',
        ...(result
          ? [{type: 'Vehicle' as const, id: result.vehicleId}, 'SaleVehicle' as const]
          : []),
      ],
    },
    changeFactoryReference: {
      invalidatesTags: invalidateOnSuccess((result: VehicleOrderDetailResponseBody | undefined) => [
        'NewVehicleOrder',
        'NewVehicleConfiguration',
        ...(result
          ? [{type: 'Vehicle' as const, id: result.vehicleId}, 'SaleVehicle' as const]
          : []),
      ]),
    },
    applyImporterStatus: {
      invalidatesTags: ['NewVehicleOrder', 'NewVehicleConfiguration'],
    },
    getConfigurationByVehicle: {
      providesTags: ['NewVehicleConfiguration', 'NewVehicleOrder'],
    },
    commitConfigurationToBusinessCase: {
      invalidatesTags: ['NewVehicleConfiguration', 'NewVehicleOrder'],
    },
    listMake: {providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring']},
    createMake: {
      invalidatesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'],
    },
    getMake: {providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring']},
    updateMake: {
      invalidatesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'],
    },
    archiveMake: {
      invalidatesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'],
    },
    listModel: {providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring']},
    createModel: {
      invalidatesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'],
    },
    getModel: {providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring']},
    updateModel: {
      invalidatesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'],
    },
    archiveModel: {
      invalidatesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'],
    },
    listModelYear: {
      providesTags: [
        'NewVehicleCatalogue',
        'NewVehicleCatalogueAuthoring',
        resourceTag('modelYear'),
        'NewVehicleModelYearReference',
      ],
    },
    createModelYear: {
      invalidatesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'],
    },
    getModelYear: {
      providesTags: [
        'NewVehicleCatalogue',
        'NewVehicleCatalogueAuthoring',
        resourceTag('modelYear'),
        'NewVehicleModelYearReference',
      ],
    },
    getModelYearReadiness: {
      providesTags: [
        'NewVehicleCatalogue',
        'NewVehicleCatalogueAuthoring',
        resourceTag('readiness'),
      ],
    },
    updateModelYear: {
      invalidatesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'],
    },
    archiveModelYear: {
      invalidatesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'],
    },
    listTrim: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('trim')],
    },
    createTrim: pricedItemWrite(
      (arg: CreateTrimApiArg) => arg.trimWriteRequestBody.pricelistVersion,
      'trim',
      'variant',
      'matrix',
      'modelYear'
    ),
    getTrim: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('trim')],
    },
    // A status switch on a published model year carries a pricelist version (T20-142450).
    updateTrim: pricedItemWrite(
      (arg: UpdateTrimApiArg) => arg.trimWriteRequestBody.pricelistVersion,
      'trim'
    ),
    archiveTrim: pricedItemWrite(
      (arg: ArchiveTrimApiArg) => arg.pricedItemArchiveRequestBody.pricelistVersion,
      'trim',
      'variant',
      'matrix',
      'assignment',
      'colour',
      'upholstery',
      'feature'
    ),
    listEngine: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('engine')],
    },
    createEngine: catalogueWrite('engine', 'variant', 'matrix'),
    getEngine: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('engine')],
    },
    updateEngine: lockedChildWrite('engine'),
    archiveEngine: lockedChildWrite('engine', 'variant', 'matrix'),
    listVariant: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('variant')],
    },
    createVariant: pricedItemWrite(
      (arg: CreateVariantApiArg) => arg.variantItemWriteRequestBody.pricelistVersion,
      'variant',
      'matrix'
    ),
    getVariant: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('variant')],
    },
    // An enable switch of a Variant of an online trim carries a pricelist version (T20-142450).
    updateVariant: pricedItemWrite(
      (arg: UpdateVariantApiArg) => arg.variantItemWriteRequestBody.pricelistVersion,
      'variant',
      'matrix'
    ),
    archiveVariant: pricedItemWrite(
      (arg: ArchiveVariantApiArg) => arg.pricedItemArchiveRequestBody.pricelistVersion,
      'variant',
      'matrix'
    ),
    listColour: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('colour')],
    },
    createColour: catalogueWrite('colour', 'restriction'),
    getColour: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('colour')],
    },
    updateColour: lockedChildWrite('colour', 'restriction'),
    archiveColour: pricedItemWrite(
      (arg: ArchiveColourApiArg) => arg.pricedItemArchiveRequestBody.pricelistVersion,
      'colour',
      'restriction'
    ),
    listUpholstery: {
      providesTags: [
        'NewVehicleCatalogue',
        'NewVehicleCatalogueAuthoring',
        resourceTag('upholstery'),
      ],
    },
    createUpholstery: pricedItemWrite(
      (arg: CreateUpholsteryApiArg) => arg.upholsteryWriteRequestBody.pricelistVersion,
      'upholstery',
      'restriction',
      'modelYear'
    ),
    getUpholstery: {
      providesTags: [
        'NewVehicleCatalogue',
        'NewVehicleCatalogueAuthoring',
        resourceTag('upholstery'),
      ],
    },
    updateUpholstery: lockedChildWrite('upholstery', 'restriction'),
    archiveUpholstery: pricedItemWrite(
      (arg: ArchiveUpholsteryApiArg) => arg.pricedItemArchiveRequestBody.pricelistVersion,
      'upholstery',
      'trim',
      'restriction'
    ),
    listFeature: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('feature')],
    },
    createFeature: catalogueWrite('feature'),
    getFeature: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('feature')],
    },
    updateFeature: catalogueWrite('feature', 'assignment'),
    archiveFeature: catalogueWrite('feature', 'assignment', 'package', 'rule'),
    listPackage: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('package')],
    },
    createPackage: pricedItemWrite(
      (arg: CreatePackageApiArg) => arg.packageWriteRequestBody.pricelistVersion,
      'package',
      'feature',
      'modelYear'
    ),
    getPackage: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('package')],
    },
    // A status switch on a published model year carries a pricelist version (T20-142450).
    updatePackage: pricedItemWrite(
      (arg: UpdatePackageApiArg) => arg.packageWriteRequestBody.pricelistVersion,
      'package',
      'feature'
    ),
    archivePackage: pricedItemWrite(
      (arg: ArchivePackageApiArg) => arg.pricedItemArchiveRequestBody.pricelistVersion,
      'package',
      'feature',
      'rule'
    ),
    getMakeOrderStatusMapping: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'],
    },
    getMakeOrderStatusSignalMapping: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'],
    },
    listUnmappedMakeStatus: {providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring']},
    publishModelYear: {
      invalidatesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'],
    },
    unpublishModelYear: {
      invalidatesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'],
    },
    duplicateModelYear: {
      invalidatesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'],
    },
    getVariantMatrix: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('matrix')],
    },
    putVariantMatrix: pricedItemWrite(
      (arg: PutVariantMatrixApiArg) => arg.variantMatrixWriteRequestBody.pricelistVersion,
      'variant',
      'matrix',
      'modelYear'
    ),
    patchVariantMatrixCell: pricedItemWrite(
      (arg: PatchVariantMatrixCellApiArg) => arg.variantItemWriteRequestBody.pricelistVersion,
      'variant',
      'matrix',
      'modelYear'
    ),
    bulkUpdateVariantMatrix: catalogueWrite('variant', 'matrix'),
    applyMidYearPriceAdjustment: {
      invalidatesTags: invalidateOnSuccess((_result, arg: ApplyMidYearPriceAdjustmentApiArg) =>
        arg.priceAdjustmentRequestBody.previewOnly
          ? []
          : ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring']
      ),
    },
    /**
     * A batch can write any list of the year, the label and History, so a successful one invalidates
     * both broad tags. A failed batch wrote nothing (it is atomic).
     */
    applyPricedItemChangeBatch: {
      invalidatesTags: invalidateOnSuccess(['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring']),
    },
    listPricelistVersionHistory: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('matrix')],
    },
    cancelPricelistVersion: {
      invalidatesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'],
    },
    getAuthorConfiguratorBundle: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('bundle')],
    },
    evaluateAuthorConfiguration: {
      providesTags: [
        'NewVehicleCatalogue',
        'NewVehicleCatalogueAuthoring',
        resourceTag('evaluate'),
      ],
    },
    listTrimFeatureAssignments: {
      providesTags: (_result, _error, {trimId}) => [
        'NewVehicleCatalogue',
        'NewVehicleCatalogueAuthoring',
        resourceTag('assignment'),
        trimAssignmentTag(trimId),
      ],
    },
    // The PUT replaces one trim's set and touches no other trim, so only that trim's list refetches.
    // Writes that can change every trim's set (editing or archiving an equipment item) invalidate 'assignment'.
    // A PUT that carried a new pricelist version also rewrote the model year and added a History
    // entry (D88), so a successful one invalidates both broad tags, as pricedItemWrite does.
    putTrimFeatureAssignments: {
      invalidatesTags: (_result, error, {trimId, replaceTrimFeatureAssignmentsRequestBody}) => [
        trimAssignmentTag(trimId),
        resourceTag('feature'),
        ...TRIM_WRITE_TAGS,
        ...(isNil(error) && isNotNil(replaceTrimFeatureAssignmentsRequestBody.pricelistVersion)
          ? (['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'] as const)
          : []),
      ],
    },
    putTrimUpholstery: lockedChildWrite('trim', 'upholstery'),
    getColourUpholsteryRestrictions: {
      providesTags: [
        'NewVehicleCatalogue',
        'NewVehicleCatalogueAuthoring',
        resourceTag('restriction'),
      ],
    },
    putColourUpholsteryRestrictions: catalogueWrite('restriction'),
    // A PUT that adds or drops rows of online trims carries a new pricelist version (D102).
    putColourTrims: pricedItemWrite(
      (arg: PutColourTrimsApiArg) => arg.replaceColourTrimsRequestBody.pricelistVersion,
      'colour'
    ),
    listFeatureRule: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('rule')],
    },
    createFeatureRule: lockedChildWrite('rule'),
    getFeatureRule: {
      providesTags: ['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring', resourceTag('rule')],
    },
    updateFeatureRule: lockedChildWrite('rule'),
    deleteFeatureRule: lockedChildWrite('rule'),
    detachCatalogueImage: {invalidatesTags: catalogueImageTags},
  },
});

export const newVehicleUploadApi = newVehicleApi.injectEndpoints({
  endpoints: (build) => ({
    uploadCatalogueImage: build.mutation<
      UploadCatalogueImageApiResponse,
      UploadCatalogueImageApiArg & {body: {file: Blob; version: number}}
    >({
      query: (queryArg) => {
        const formData = new FormData();
        formData.append('file', queryArg.body.file);
        formData.append('version', queryArg.body.version.toString());
        return {
          url: `/dms/v1/new-vehicle/catalogue/${queryArg.owner}/${queryArg.ownerId}/image`,
          method: 'POST',
          body: formData,
          headers: {
            'X-Authorization-Profile': queryArg['X-Authorization-Profile'],
          },
        };
      },
      invalidatesTags: catalogueImageTags,
    }),
  }),
  overrideExisting: true,
});

export const {useUploadCatalogueImageMutation} = newVehicleUploadApi;

const newVehicleOfferSheetApi = newVehicleApi.injectEndpoints({
  endpoints: (build) => ({
    exportConfigurationOfferSheet: build.mutation<
      string | Blob,
      ExportConfigurationOfferSheetApiArg & {accept: 'text/html' | 'application/pdf'}
    >({
      query: (queryArg) => ({
        url: `/dms/v1/new-vehicle/configuration/${queryArg.configurationId}/offer-sheet`,
        method: 'POST',
        body: queryArg.exportConfigurationOfferSheetRequestBody,
        headers: {Accept: queryArg.accept},
        responseHandler: (response) =>
          queryArg.accept === 'application/pdf' ? response.blob() : response.text(),
      }),
    }),
  }),
  overrideExisting: true,
});

export const {useExportConfigurationOfferSheetMutation} = newVehicleOfferSheetApi;

export const {
  useGetCatalogueTreeQuery,
  useGetConfiguratorBundleQuery,
  useGetConfigurationDetailQuery,
  useLazyGetConfigurationDetailQuery,
  useUpdateConfigurationMutation,
  useDeleteConfigurationMutation,
  useGetConfigurationCustomerViewQuery,
  useListInterestConfigurationsQuery,
  useListBusinessCaseConfigurationsQuery,
  useEvaluateConfigurationQuery,
  useCreateConfigurationMutation,
  useCopyConfigurationMutation,
  useRepriceConfigurationMutation,
  useCommitConfigurationToBusinessCaseMutation,
  useOrderConfigurationMutation,
  useRegisterExternalOrderMutation,
  useGetVehicleOrderQuery,
  useGetVehicleOrderFactoryRecordQuery,
  useUpdateOrderStatusMutation,
  useCancelOrderMutation,
  useChangeFactoryReferenceMutation,
  useRecordOrderArrivalMutation,
  useHandoverOrderMutation,
  useApplyImporterStatusMutation,
  useGetConfigurationByVehicleQuery,
  useListMakeQuery,
  useCreateMakeMutation,
  useGetMakeQuery,
  useUpdateMakeMutation,
  useArchiveMakeMutation,
  useListModelQuery,
  useCreateModelMutation,
  useGetModelQuery,
  useUpdateModelMutation,
  useArchiveModelMutation,
  useListModelYearQuery,
  useCreateModelYearMutation,
  useGetModelYearQuery,
  useUpdateModelYearMutation,
  useArchiveModelYearMutation,
  useListTrimQuery,
  useCreateTrimMutation,
  useGetTrimQuery,
  useUpdateTrimMutation,
  useArchiveTrimMutation,
  useListEngineQuery,
  useCreateEngineMutation,
  useGetEngineQuery,
  useUpdateEngineMutation,
  useArchiveEngineMutation,
  useListVariantQuery,
  useCreateVariantMutation,
  useGetVariantQuery,
  useUpdateVariantMutation,
  useArchiveVariantMutation,
  useListColourQuery,
  useCreateColourMutation,
  useGetColourQuery,
  useUpdateColourMutation,
  useArchiveColourMutation,
  useListUpholsteryQuery,
  useCreateUpholsteryMutation,
  useGetUpholsteryQuery,
  useUpdateUpholsteryMutation,
  useArchiveUpholsteryMutation,
  useListFeatureQuery,
  useCreateFeatureMutation,
  useGetFeatureQuery,
  useUpdateFeatureMutation,
  useArchiveFeatureMutation,
  useListPackageQuery,
  useCreatePackageMutation,
  useGetPackageQuery,
  useUpdatePackageMutation,
  useArchivePackageMutation,
  useGetMakeOrderStatusMappingQuery,
  useGetMakeOrderStatusSignalMappingQuery,
  useListUnmappedMakeStatusQuery,
  useGetModelYearReadinessQuery,
  usePublishModelYearMutation,
  useUnpublishModelYearMutation,
  useDuplicateModelYearMutation,
  useGetVariantMatrixQuery,
  usePutVariantMatrixMutation,
  usePatchVariantMatrixCellMutation,
  useBulkUpdateVariantMatrixMutation,
  useApplyMidYearPriceAdjustmentMutation,
  useApplyPricedItemChangeBatchMutation,
  useListPricelistVersionHistoryQuery,
  useCancelPricelistVersionMutation,
  useGetAuthorConfiguratorBundleQuery,
  useEvaluateAuthorConfigurationQuery,
  useListTrimFeatureAssignmentsQuery,
  usePutTrimFeatureAssignmentsMutation,
  usePutTrimUpholsteryMutation,
  useGetColourUpholsteryRestrictionsQuery,
  usePutColourUpholsteryRestrictionsMutation,
  usePutColourTrimsMutation,
  useListFeatureRuleQuery,
  useCreateFeatureRuleMutation,
  useGetFeatureRuleQuery,
  useUpdateFeatureRuleMutation,
  useDeleteFeatureRuleMutation,
  useDetachCatalogueImageMutation,
} = newVehicleApi;
